import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FileSpreadsheet, DollarSign, SlidersHorizontal, ListChecks, Download } from 'lucide-react';
import ExportFilterModal from '../components/ExportFilterModal';
import EarlyArrivalsModal from '../components/EarlyArrivalsModal';
import { useLanguage } from '../context/LanguageContext';

// Emitir los documentos de un periodo. Vive aparte de Historial a proposito: aquella pantalla es de
// CONSULTA (tabla y filtros, solo lectura) y esta ESCRIBE — revisa las horas, recalcula la semana
// con lo que se confirme y de ahi saca el archivo. Mezclarlas hacia que una pantalla llamada
// "Historial" acabara recalculando nomina.
//
// No tiene filtros propios: el modal de exportacion pide periodo, facilidad y alcance por su cuenta,
// asi que aqui no hay un estado de pantalla que pueda contradecir al del modal.
export default function GenerateReports() {
  const { t } = useLanguage();
  const [loadingExportTC, setLoadingExportTC] = useState(false);
  const [loadingExportPR, setLoadingExportPR] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportModalType, setExportModalType] = useState('');
  const [earlyArrivalsModalOpen, setEarlyArrivalsModalOpen] = useState(false);
  // Employees with their clock-in days, as returned by the scan: what the calculation modal edits.
  const [scanEmployees, setScanEmployees] = useState([]);
  // El alcance sobre el que va a correr el calculo: { dateFrom, dateTo, facility, badges }.
  const [pendingCalcRange, setPendingCalcRange] = useState(null);
  // La exportacion que espera del otro lado de la revision, { type, filters }.
  const [pendingExport, setPendingExport] = useState(null);

  const openExportModal = (type) => {
    setExportModalType(type);
    setExportModalOpen(true);
  };

  // Solo la descarga. La revision de horas que la precede vive en handleExportRequest.
  const doExport = async (type, filters = {}) => {
    const from = filters.dateFrom;
    const to = filters.dateTo;
    if (!from || !to) {
      Swal.fire({ icon: 'warning', title: t('rec.datesRequired'), text: t('rec.datesRequiredMsg') });
      return;
    }
    const setLoadFn = type === 'timecards' ? setLoadingExportTC : setLoadingExportPR;
    setLoadFn(true);
    try {
      const extraParams = {};
      if (filters.facility) extraParams.facility = filters.facility;
      if (filters.employeeType) extraParams.empType = filters.employeeType;
      if (filters.badgeNumber) extraParams.badge = filters.badgeNumber;
      if (filters.company) extraParams.company = filters.company;
      const suffixParts = [];
      if (filters.employeeType) suffixParts.push(filters.employeeType);
      else if (filters.badgeNumber) suffixParts.push(`Badge${filters.badgeNumber}`);
      if (filters.company) suffixParts.push(filters.company.replace(/[^\w-]+/g, '_'));
      const filterSuffix = suffixParts.length ? `_${suffixParts.join('_')}` : '';
      const isExcel = filters.format === 'excel';
      const ext = isExcel ? 'xlsx' : 'pdf';
      const mime = isExcel
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';

      if (type === 'timecards') {
        const endpoint = isExcel ? '/api/timecards/export-timecard-excel' : '/api/timecards/export-timecard';
        const res = await axios.get(endpoint, {
          params: { dateFrom: from, dateTo: to, ...extraParams },
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Timecard_${filters.facility || 'ALL'}${filterSuffix}_${from}_${to}.${ext}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        Swal.fire({ icon: 'success', title: t('common.exported'), text: t('rec.tcDownloaded'), timer: 2000, showConfirmButton: false });
      } else {
        let endpoint;
        if (filters.reportVariant === 'summary') {
          endpoint = isExcel ? '/api/timecards/export-payroll-summary-excel' : '/api/timecards/export-payroll-summary-pdf';
        } else {
          endpoint = isExcel ? '/api/timecards/export-payroll-excel' : '/api/timecards/export-payroll-pdf';
        }
        const res = await axios.get(endpoint, {
          params: { dateFrom: from, dateTo: to, ...extraParams },
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
        const link = document.createElement('a');
        link.href = url;
        const label = filters.reportVariant === 'summary' ? 'PAYROLL TOTAL HOURS' : 'PAYROLL SUMMARY';
        link.setAttribute('download', `${label} ${filters.facility || 'ALL'}${filterSuffix} week ending ${to.replace(/-/g, '.')}.${ext}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        Swal.fire({ icon: 'success', title: t('common.exported'), text: t('rec.prDownloaded'), timer: 2000, showConfirmButton: false });
      }
      setExportModalOpen(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('rec.exportError') });
      console.error(err);
    }
    setLoadFn(false);
  };

  // paidOut / lunch travel with paidIn: the modal decides the three together, and dropping two of
  // them here would hand every clock-out and every forced lunch back to the defaults on recalc.
  // `paidInBadges` es lo que hace que los NO marcados conserven sus horas y sus lunch — sin el, el
  // calculo reconstruye la semana entera y los deja con los valores por defecto.
  // `quiet` calla el aviso de exito cuando lo que sigue es una descarga: dos dialogos encadenados
  // solo estorban, y el archivo bajando ya es el acuse.
  const runRecalculate = async (range, paidIn, paidOut, lunch, badges, quiet = false) => {
    Swal.fire({
      title: t('rep.recalculating'), html: t('rep.calculating'),
      allowOutsideClick: false, allowEscapeKey: false, didOpen: () => Swal.showLoading(),
    });
    try {
      const calcRes = await axios.post('/api/timecards/calculate',
        {
          dateFrom: range.dateFrom, dateTo: range.dateTo, facility: range.facility,
          paidIn, paidInBadges: badges, paidOut, lunch,
        },
        { timeout: 300000 });
      const warnings = calcRes.data.warnings || [];
      if (quiet && warnings.length === 0) {
        Swal.close();
      } else {
        const warningsHtml = warnings.length > 0
          ? '<br><br><span style="color:#ef4444;font-weight:700">' + warnings.join('<br>') + '</span>'
          : '';
        const rangeHtml = `<br>${t('rep.period')} ${range.dateFrom} a ${range.dateTo} (${range.facility || ''})`;
        await Swal.fire({
          icon: warnings.length > 0 ? 'warning' : 'success',
          title: t('rep.recalcComplete'),
          html: `<b>${calcRes.data.calculated}</b> ${t('rep.recalcMsg')}${rangeHtml}${warningsHtml}`,
        });
      }
      return true;
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('rep.recalcError') });
      return false;
    }
  };

  // Escanear el periodo y abrir la revision de horas. `exportJob` viaja hasta la confirmacion: al
  // terminar el calculo se descarga el archivo que lo trajo hasta aqui.
  const openReview = async (range, exportJob) => {
    Swal.fire({
      title: t('early.scanning'), html: t('early.scanning'),
      allowOutsideClick: false, allowEscapeKey: false, showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      const scan = await axios.post('/api/timecards/scan-early-arrivals', range);
      const empList = scan.data.employees || [];
      if (empList.length === 0) {
        // Nadie marco en el periodo: no hay entrada, salida ni comida que decidir.
        const ok = await runRecalculate(range, {}, {}, {}, range.badges, true);
        if (ok) await doExport(exportJob.type, exportJob.filters);
        return;
      }
      Swal.close();
      setScanEmployees(empList);
      setPendingCalcRange(range);
      setPendingExport(exportJob);
      setEarlyArrivalsModalOpen(true);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('rep.recalcError') });
    }
  };

  // Generar un reporte pasa primero por la revision de horas, igual que el proceso de Importar: se
  // escanea el periodo elegido, se confirma con que hora se paga cada dia, se recalcula, y de ahi
  // sale el archivo. Lo que se descarga es entonces lo que se acaba de revisar.
  const handleExportRequest = async (type, filters = {}) => {
    if (!filters.dateFrom || !filters.dateTo) {
      Swal.fire({ icon: 'warning', title: t('rec.datesRequired'), text: t('rec.datesRequiredMsg') });
      return;
    }
    setExportModalOpen(false);
    // Un reporte de UNA persona revisa solo a esa persona. En los demas modos el alcance es la
    // planta del periodo, que es lo que el archivo va a cubrir de todos modos.
    const badges = filters.badgeNumber ? [String(filters.badgeNumber)] : undefined;
    await openReview(
      { dateFrom: filters.dateFrom, dateTo: filters.dateTo, facility: filters.facility || undefined, badges },
      { type, filters }
    );
  };

  const handleEarlyArrivalsConfirm = async ({ paidIn, paidOut, lunch, badges }) => {
    const range = pendingCalcRange;
    const job = pendingExport;
    setEarlyArrivalsModalOpen(false);
    setScanEmployees([]);
    setPendingCalcRange(null);
    setPendingExport(null);
    if (!range || !job) return;
    const ok = await runRecalculate(range, paidIn, paidOut, lunch, badges, true);
    if (ok) await doExport(job.type, job.filters);
  };

  // Cerrar la revision cancela tambien la descarga que esperaba detras: se salio del proceso, y
  // bajar el archivo sin las horas confirmadas seria justo lo que este paso viene a evitar.
  const handleEarlyArrivalsClose = () => {
    setEarlyArrivalsModalOpen(false);
    setScanEmployees([]);
    setPendingCalcRange(null);
    setPendingExport(null);
  };

  const steps = [
    { icon: <SlidersHorizontal size={18} strokeWidth={1.9} />, title: t('gen.step1'), desc: t('gen.step1Desc') },
    { icon: <ListChecks size={18} strokeWidth={1.9} />, title: t('gen.step2'), desc: t('gen.step2Desc') },
    { icon: <Download size={18} strokeWidth={1.9} />, title: t('gen.step3'), desc: t('gen.step3Desc') },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('gen.title')}</h1>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <span className="card-title">{t('gen.pick')}</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13.5, lineHeight: 1.5, marginBottom: 18 }}>
          {t('gen.intro')}
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn-primary" style={{ width: 'auto', padding: '10px 24px' }}
            onClick={() => openExportModal('timecards')} disabled={loadingExportTC}>
            <FileSpreadsheet size={15} style={{ marginRight: 6 }} />
            {loadingExportTC ? t('common.exporting') : t('rep.generateTimecards')}
          </button>
          <button className="btn-primary" style={{ width: 'auto', padding: '10px 24px', background: 'var(--warning)' }}
            onClick={() => openExportModal('payroll')} disabled={loadingExportPR}>
            <DollarSign size={15} style={{ marginRight: 6 }} />
            {loadingExportPR ? t('common.exporting') : t('rep.generatePayrolls')}
          </button>
        </div>
      </div>

      {/* El proceso a la vista: esta pantalla RECALCULA antes de descargar, y eso conviene que se
          sepa antes de darle al boton, no despues. */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">{t('gen.how')}</span>
        </div>
        <div style={{ display: 'grid', gap: 14 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span className="exp-step-num" style={{ marginTop: 2 }}>{i + 1}</span>
              <span style={{ color: 'var(--accent)', display: 'flex', marginTop: 1 }}>{s.icon}</span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{s.title}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>{s.desc}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <ExportFilterModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        exportType={exportModalType}
        loading={exportModalType === 'timecards' ? loadingExportTC : loadingExportPR}
        onExport={(filters) => handleExportRequest(exportModalType, filters)}
        showDateRange
      />

      <EarlyArrivalsModal
        isOpen={earlyArrivalsModalOpen}
        onClose={handleEarlyArrivalsClose}
        employees={scanEmployees}
        onConfirm={handleEarlyArrivalsConfirm}
      />
    </div>
  );
}
